import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
  useRef,
} from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import { useTranslation } from 'react-i18next';

import { CookieManager } from '../../utils/cookieManager';
import { debugApiRequests } from '../../utils/debugApi';
import { TokenUtils } from '../../utils/tokenUtils';
import { setUserQueryData, clearUserQueryData } from 'hooks/useCurrentUser';
import { useToast } from 'hooks/useToast';
import { clearPersistedCache, getCachedUserId } from 'lib/query';
import { apiOrchestrator, setupStrapiHeaders } from 'services/apiService';
import { UserData } from 'types/userData';
import { UserPermissions } from 'types/userPlanFeaturesTypes';
import { clearHeaders, validateAndConfigureHeaders } from 'utils/headersManager';
import { logger } from 'utils/logger';
import { isTokenValid } from 'utils/tokenValidation';

interface UserContextProps {
  user: UserData | null;
  setUser: React.Dispatch<React.SetStateAction<UserData | null>>;
  token: string | null;
  setToken: (token: string) => void;
  setTokenSafe: (value: string | null, reason: string) => void;
  logout: () => void;
  handleSetUserId: (userId: string) => void;
  permissions: UserPermissions;
  setPermissions: (permissions: UserPermissions) => void;
  initializeUserContext: (userId: string, token: string) => Promise<void>;
  isLoading: boolean;
}

const UserContext = createContext<UserContextProps | undefined>(undefined);

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const navigate = useNavigate();
  const { toastError } = useToast();
  const { t } = useTranslation('toast');
  const queryClient = useQueryClient();

  const [user, setUser] = useState<UserData | null>(null);
  const [token, setToken] = useState<string | null>(() => {
    // Try to get token from cookie first, then from localStorage
    const cookieToken = CookieManager.getAccessToken();
    const localToken = localStorage.getItem('AUTH_TOKEN');
    return cookieToken || localToken;
  });
  const [userId, setUserId] = useState<string | null>(() => localStorage.getItem('user_id'));
  const [permissions, setPermissions] = useState<UserPermissions>({});
  const [isLoading, setIsLoading] = useState(true);
  const isLoggingOutRef = useRef(false); // Global flag to prevent multiple logouts

  useEffect(() => {
    if (token && userId) {
      apiOrchestrator.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token, userId]);

  const handleSetUserId = (newUserId: string) => {
    setUserId(newUserId);
    localStorage.setItem('user_id', newUserId);
    logger.debug('[UserProvider] user_id saved:', { data: newUserId });
  };

  // Function to set the token with reason logging (memoized)
  const setTokenSafe = useCallback(
    (value: string | null, reason: string) => {
      logger.debug('[UserContext] setTokenSafe chamado:', {
        hasValue: !!value,
        reason,
        previousToken: !!localStorage.getItem('AUTH_TOKEN'),
        currentPath: window.location.pathname,
      });

      setToken(value);
      if (value) {
        localStorage.setItem('AUTH_TOKEN', value);
        // Also save in cookie
        CookieManager.setAccessToken(value);
        logger.debug(
          '[UserProvider] Token saved to localStorage and cookie:',
          value,
          '| Reason:',
          reason
        );
      } else {
        localStorage.removeItem('AUTH_TOKEN');
        // Clear cookie too
        CookieManager.removeAccessToken();
        logger.debug('[UserProvider] Token removed from localStorage and cookie! | Reason:', {
          data: reason,
        });
      }
    },
    [setToken]
  );

  const initializeUserContext = async (userId: string, token: string) => {
    logger.debug('[UserContext] Initializing user context...', {
      userId,
      hasToken: !!token,
      currentPath: window.location.pathname,
    });

    // Check for user switch and clear cache if different user
    const cachedUserId = getCachedUserId();
    if (cachedUserId && cachedUserId !== userId) {
      logger.debug('[UserContext] User switch detected, clearing cache', {
        previousUserId: cachedUserId,
        newUserId: userId,
      });
      clearPersistedCache();
    }

    // Debug API configurations
    debugApiRequests();

    try {
      setIsLoading(true);

      // 1. Configure token and userId
      setTokenSafe(token, 'login');
      handleSetUserId(userId);

      // 2. Get required headers from localStorage
      const organizationUUID = localStorage.getItem('x-organization-uuid');
      const cognitoUserPool = localStorage.getItem('x-cognito-userpool');

      logger.debug('[UserContext] Headers do localStorage:', {
        hasOrganizationUUID: !!organizationUUID,
        hasCognitoUserPool: !!cognitoUserPool,
      });

      if (!organizationUUID || !cognitoUserPool) {
        logger.error('[UserContext] Required headers not found:', {
          organizationUUID,
          cognitoUserPool,
        });
        throw new Error('Required headers not found');
      }

      // 3. Configure Strapi headers
      if (!setupStrapiHeaders(token, organizationUUID, cognitoUserPool)) {
        logger.error('[UserContext] Failed to configure Strapi headers');
        throw new Error('Failed to configure Strapi headers');
      }

      // 4. Configure headers for other APIs
      const isValid = await validateAndConfigureHeaders({
        token,
        organizationUUID,
        cognitoUserPool,
      });

      if (!isValid) {
        logger.error('[UserContext] Failed to configure API headers');
        throw new Error('Failed to configure headers');
      }

      // 5. Fetch user data
      logger.debug('[UserContext] Fetching user data...');
      const userData = await apiOrchestrator.get<UserData>('/auth/me');
      logger.debug('[UserContext] User data received:', { data: userData.data });

      // Set user directly with orchestrator data
      setUser(userData.data);

      // 6. Sync user data with React Query cache
      setUserQueryData(queryClient, userData.data);
      logger.debug('[UserContext] User data synced with React Query cache');

      logger.debug('[UserContext] User context initialized successfully!');
    } catch (error) {
      logger.error('[UserContext] Error initializing user context:', {
        error,
        errorMessage: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined,
        userId,
        hasToken: !!token,
        currentPath: window.location.pathname,
      });
      // toastError('Error loading user data.');
      logger.error('Error loading user data:', error);
      logout();
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = useCallback(async () => {
    if (isLoggingOutRef.current) return;
    isLoggingOutRef.current = true;
    try {
      // API call with timeout
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);
        await apiOrchestrator.post('/auth/logout', null, { signal: controller.signal });
        clearTimeout(timeoutId);
      } catch (apiError) {
        logger.warn('[UserContext] Failed to call /auth/logout (timeout or error):', {
          data: apiError,
        });
      }

      // Dispatch logout event
      window.dispatchEvent(new CustomEvent('logout'));

      // Clear all state
      setUser(null);
      setTokenSafe(null, 'logout');
      setUserId(null);

      // Clear localStorage
      localStorage.removeItem('userData');
      localStorage.removeItem('AUTH_TOKEN');
      localStorage.removeItem('user_id');
      localStorage.removeItem('x-cognito-userpool');
      localStorage.removeItem('x-organization-uuid');
      localStorage.removeItem('menuData');
      localStorage.removeItem('selectedMenus');

      // Clear React Query cache
      clearUserQueryData(queryClient);
      clearPersistedCache();

      // Clear cookies and headers
      CookieManager.clearAllAuthCookies();
      clearHeaders();

      // Navigate to login with fallback
      try {
        navigate('/login', { replace: true });
      } catch {
        window.location.href = '/login';
      }
    } catch (error) {
      logger.error('[UserContext] Error during logout:', error);
      toastError(t('auth.sessionEndError'));
      // Even on error, force redirect to login
      try {
        navigate('/login', { replace: true });
      } catch {
        window.location.href = '/login';
      }
    } finally {
      isLoggingOutRef.current = false;
    }
  }, [navigate, toastError, setTokenSafe, queryClient]);

  useEffect(() => {
    const storedToken = localStorage.getItem('AUTH_TOKEN');
    const storedUserId = localStorage.getItem('user_id');

    logger.debug('[UserContext] useEffect initialization:', {
      hasStoredToken: !!storedToken,
      hasStoredUserId: !!storedUserId,
      currentToken: !!token,
      isLoading,
      currentPath: window.location.pathname,
      timestamp: new Date().toISOString(),
    });

    if (storedToken && storedUserId) {
      // VALIDATE TOKEN BEFORE USING IT
      if (isTokenValid(storedToken)) {
        logger.debug('[UserContext] Token valid, initializing context with localStorage data');
        initializeUserContext(storedUserId, storedToken).catch(error => {
          logger.error('[UserContext] Error during initialization:', error);
          logout();
        });
      } else {
        // Token is expired or invalid - logout immediately
        logger.warn(
          '[UserContext] Expired or invalid token detected, executing immediate logout'
        );
        logout();
        setIsLoading(false);
      }
    } else {
      logger.debug('[UserContext] No data in localStorage, not initializing');
      setIsLoading(false);
    }
  }, []); // Empty dependency to execute only once

  // Check token health periodically and try proactive refresh
  useEffect(() => {
    const isRefreshingRef = { current: false };

    const checkTokenHealthInterval = setInterval(async () => {
      if (!token || isRefreshingRef.current) return;

      const validation = TokenUtils.validateToken(token);

      if (!validation.isValid || validation.isExpired) {
        logger.warn(
          '[UserContext] Invalid/expired token detected in health check, trying refresh...'
        );
        isRefreshingRef.current = true;
        try {
          const { RefreshTokenService } = await import('../../services/refreshTokenService');
          const newToken = await RefreshTokenService.refreshAccessToken();
          if (newToken) {
            setTokenSafe(newToken, 'health-check-refresh');
            logger.debug('[UserContext] Token renewed successfully via health check');
          } else {
            logger.warn('[UserContext] Refresh returned null, forcing logout');
            logout();
          }
        } catch (error) {
          logger.error('[UserContext] Refresh failed via health check, forcing logout:', error);
          logout();
        } finally {
          isRefreshingRef.current = false;
        }
      } else if (validation.timeUntilExpiry && validation.timeUntilExpiry < 5 * 60 * 1000) {
        // Token expiring in less than 5 minutes - proactive refresh
        logger.debug('[UserContext] Token expiring soon, trying proactive refresh...');
        isRefreshingRef.current = true;
        try {
          const { RefreshTokenService } = await import('../../services/refreshTokenService');
          const newToken = await RefreshTokenService.refreshAccessToken();
          if (newToken) {
            setTokenSafe(newToken, 'proactive-refresh');
          }
        } catch {
          logger.warn('[UserContext] Proactive refresh failed (will retry later)');
        } finally {
          isRefreshingRef.current = false;
        }
      }
    }, 60000);

    return () => clearInterval(checkTokenHealthInterval);
  }, [token, logout, setTokenSafe]);

  // Listener for forced logout (when refresh token fails)
  useEffect(() => {
    const handleForceLogout = (event: CustomEvent) => {
      logger.debug('[UserContext] Forced logout detected:', {
        reason: event.detail?.reason || 'Reason not specified',
        error: event.detail?.error || 'No specific error',
        timestamp: event.detail?.timestamp || new Date().toISOString(),
      });

      // Execute logout immediately
      logout();
    };

    // Add listener for forced logout event
    window.addEventListener('forceLogout', handleForceLogout as EventListener);

    return () => {
      // Remove listener on unmount
      window.removeEventListener('forceLogout', handleForceLogout as EventListener);
    };
  }, [logout]);

  return (
    <UserContext.Provider
      value={{
        user,
        setUser,
        token,
        setToken,
        setTokenSafe,
        logout,
        handleSetUserId,
        permissions,
        setPermissions,
        initializeUserContext,
        isLoading,
      }}
    >
      {children}
    </UserContext.Provider>
  );
};

export const useUser = (): UserContextProps => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};
